import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FocusScope } from '@react-aria/focus';
import { DismissButton } from '@react-aria/overlays';
import cn from 'classnames';

import { DEFAULT_STYLES } from '../../constants';
import {
  readBodyInlineOverflow,
  writeBodyInlineOverflow
} from '../../utils/bodyInlineOverflow';
import { DropdownPlacement, SelectProps } from '../../types';

import { BottomSheet } from '../BottomSheet';
import { FieldErrorMessage } from '../FieldErrorMessage';
import { Portal } from '../Portal';

// Custom hook for dropdown positioning
function useDropdownPosition(
  triggerRef: React.RefObject<HTMLElement>,
  overlayRef: React.RefObject<HTMLElement>,
  isOpen: boolean,
  placement: DropdownPlacement = 'bottom',
  offset = 4
) {
  const [position, setPosition] = useState<React.CSSProperties>({});

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !isOpen) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Get overlay dimensions if available
    // Use getBoundingClientRect for actual rendered height (accounts for maxHeight)
    const overlayRect = overlayRef.current?.getBoundingClientRect();
    const overlayHeight =
      overlayRect?.height || overlayRef.current?.offsetHeight || 240;
    const overlayWidth =
      overlayRect?.width ||
      overlayRef.current?.offsetWidth ||
      triggerRect.width;

    let top: number | undefined;
    let bottom: number | undefined;
    let left: number;

    // Calculate base position
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    // Determine vertical placement
    let actualPlacement = placement;
    if (placement === 'auto') {
      actualPlacement =
        spaceBelow >= overlayHeight || spaceBelow >= spaceAbove
          ? 'bottom'
          : 'top';
    } else if (
      placement.startsWith('bottom') &&
      spaceBelow < overlayHeight &&
      spaceAbove > spaceBelow
    ) {
      actualPlacement = placement.replace('bottom', 'top') as DropdownPlacement;
    } else if (
      placement.startsWith('top') &&
      spaceAbove < overlayHeight &&
      spaceBelow > spaceAbove
    ) {
      actualPlacement = placement.replace('top', 'bottom') as DropdownPlacement;
    }

    // Calculate position - use top when appearing below, bottom when appearing above
    if (actualPlacement.startsWith('bottom')) {
      // Bottom: dropdown top edge is offset pixels below trigger bottom edge
      // Use top positioning (calculate from top of viewport)
      top = triggerRect.bottom + offset;
      bottom = undefined;
    } else if (actualPlacement.startsWith('top')) {
      // Top: use bottom positioning - dropdown bottom edge is offset pixels above trigger top edge
      // bottom is measured from bottom of viewport, so: viewportHeight - (triggerTop - offset)
      top = undefined;
      bottom = viewportHeight - triggerRect.top + offset;
    } else if (
      actualPlacement.startsWith('left') ||
      actualPlacement.startsWith('right')
    ) {
      // Center vertically for left/right placements
      top = triggerRect.top + (triggerRect.height - overlayHeight) / 2;
      bottom = undefined;
    } else {
      top = triggerRect.bottom + offset;
      bottom = undefined;
    }

    // Calculate left position
    if (
      actualPlacement.includes('left') &&
      !actualPlacement.startsWith('left')
    ) {
      left = triggerRect.left;
    } else if (
      actualPlacement.includes('right') &&
      !actualPlacement.startsWith('right')
    ) {
      left = triggerRect.right - overlayWidth;
    } else if (actualPlacement.includes('start')) {
      left = triggerRect.left;
    } else if (actualPlacement.includes('end')) {
      left = triggerRect.right - overlayWidth;
    } else if (actualPlacement.startsWith('left')) {
      left = triggerRect.left - overlayWidth - offset;
    } else if (actualPlacement.startsWith('right')) {
      left = triggerRect.right + offset;
    } else {
      left = triggerRect.left;
    }

    // Ensure dropdown stays within viewport
    if (left < 8) left = 8;
    if (left + overlayWidth > viewportWidth - 8) {
      left = viewportWidth - overlayWidth - 8;
    }

    // Adjust positioning to stay within viewport
    if (top !== undefined) {
      if (top < 8) top = 8;
      if (top + overlayHeight > viewportHeight - 8) {
        top = viewportHeight - overlayHeight - 8;
      }
    }
    if (bottom !== undefined) {
      if (bottom < 8) bottom = 8;
      if (bottom + overlayHeight > viewportHeight - 8) {
        bottom = viewportHeight - overlayHeight - 8;
      }
    }

    const positionStyles: React.CSSProperties = {
      position: 'fixed',
      left: `${left + 2}px`,
      overflow: 'hidden'
    };

    if (top !== undefined) {
      positionStyles.top = `${top}px`;
    }
    if (bottom !== undefined) {
      positionStyles.bottom = `${bottom}px`;
    }

    setPosition(positionStyles);
  }, [triggerRef, overlayRef, isOpen, placement, offset]);

  // Update position when open state changes or on mount
  useLayoutEffect(() => {
    if (isOpen) {
      // Initial position calculation
      updatePosition();
      // Recalculate after dropdown is rendered to get actual height (accounts for maxHeight)
      requestAnimationFrame(() => {
        updatePosition();
      });
    }
  }, [isOpen, updatePosition]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    const handleUpdate = () => updatePosition();

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updatePosition]);

  return position;
}

export const Select: React.FC<SelectProps> = (props) => {
  const {
    name,
    label,
    value,
    placeholder = 'Select an option',
    options,
    onChange,
    onBlur,
    error,
    helperText,
    isDisabled = false,
    isRequired = false,
    className,
    optionClassName,
    labelClassName,
    // Dropdown props
    placement = 'bottom',
    offset = 4,
    maxHeight = 240,
    closeOnScroll = true,
    shouldCloseOnBlur = true,
    defaultOpen = false,
    onOpenChange,
    enableAnimation = true,
    // Mobile bottom sheet
    // Set to 0 to always show dropdown (never show bottom sheet)
    mobileBreakpoint = 0
  } = props;

  // State
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= mobileBreakpoint;
    }
    return false;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= mobileBreakpoint);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint]);

  // Refs
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const listBoxRef = useRef<HTMLUListElement>(null);
  const mobileListRef = useRef<HTMLDivElement>(null); // Ref for mobile bottom sheet scroll container
  // Lock body scroll when desktop dropdown is open (inline style only; CSS rules are unchanged)
  useEffect(() => {
    if (!(isOpen && !isMobile)) return;

    const previousInlineOverflow = readBodyInlineOverflow();
    document.body.style.setProperty('overflow', 'hidden');

    return () => {
      writeBodyInlineOverflow(previousInlineOverflow);
    };
  }, [isOpen, isMobile]);

  // Get selected option
  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value]
  );

  // Handle open/close
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isDisabled) return;
      setIsOpen(open);
      onOpenChange?.(open);
      if (!open) {
        setHighlightedIndex(-1);
        if (shouldCloseOnBlur) {
          onBlur?.();
        }
      }
    },
    [isDisabled, onOpenChange, shouldCloseOnBlur, onBlur]
  );

  // Handle selection
  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange?.(optionValue);
      handleOpenChange(false);
    },
    [onChange, handleOpenChange]
  );

  // Custom positioning hook
  const positionStyles = useDropdownPosition(
    triggerRef,
    popoverRef,
    isOpen && !isMobile,
    placement,
    offset
  );

  // Close on scroll (desktop only)
  useEffect(() => {
    if (!isOpen || isMobile || !closeOnScroll) return;

    const handleScroll = (event: Event) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;
      handleOpenChange(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, isMobile, closeOnScroll, handleOpenChange]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      handleOpenChange(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isMobile, handleOpenChange]);

  // Auto-scroll to selected option when dropdown/bottom sheet opens
  useEffect(() => {
    if (!isOpen || !value) return;

    // Use setTimeout to ensure options are fully rendered
    // requestAnimationFrame ensures DOM is ready, then small delay for rendering
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Desktop dropdown: scroll in listBoxRef
        if (!isMobile && listBoxRef.current) {
          const selectedItem = listBoxRef.current.querySelector(
            `[role="option"][aria-selected="true"]`
          ) as HTMLElement;
          if (selectedItem) {
            selectedItem.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth'
            });
          }
        }

        // Mobile bottom sheet: scroll in mobileListRef
        if (isMobile && mobileListRef.current) {
          const selectedItem = mobileListRef.current.querySelector(
            `[role="option"][aria-selected="true"]`
          ) as HTMLElement;
          if (selectedItem) {
            selectedItem.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth'
            });
          }
        }
      }, 50); // Small delay to ensure options are rendered
    });
  }, [isOpen, value, isMobile, options]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isDisabled) return;

      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (isOpen && highlightedIndex >= 0) {
            const option = options[highlightedIndex];
            if (option && !option.disabled) {
              handleSelect(option.value);
            }
          } else {
            handleOpenChange(!isOpen);
          }
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            handleOpenChange(true);
          } else {
            setHighlightedIndex((prev) => {
              const next = prev + 1;
              return next >= options.length ? 0 : next;
            });
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen) {
            handleOpenChange(true);
          } else {
            setHighlightedIndex((prev) => {
              const next = prev - 1;
              return next < 0 ? options.length - 1 : next;
            });
          }
          break;
        case 'Escape':
          event.preventDefault();
          handleOpenChange(false);
          triggerRef.current?.focus();
          break;
        case 'Tab':
          if (isOpen) {
            handleOpenChange(false);
          }
          break;
      }
    },
    [
      isDisabled,
      isOpen,
      highlightedIndex,
      options,
      handleSelect,
      handleOpenChange
    ]
  );

  const hasError = Boolean(error);
  // Hide error when dropdown is open (user is actively interacting)
  const showError = hasError && !isOpen;
  // Show helper text when no error, or when error is hidden (dropdown open)
  const showHelperText = (!hasError || isOpen) && helperText;

  // Animation classes
  const animationClasses = enableAnimation
    ? cn(
      'transition-all duration-200 ease-out',
      isOpen
        ? 'opacity-100 transform scale-100'
        : 'opacity-0 transform scale-95'
    )
    : '';

  // Render dropdown content for mobile bottom sheet
  const renderMobileContent = () => (
    <div
      ref={mobileListRef}
      className="overflow-y-auto flex-1 p-2 px-3"
      style={{ maxHeight: 'calc(80vh - 120px)', minHeight: '30vh' }}
    >
      {options.length > 0 ? (
        options.map((option) => (
          <div
            key={option.value}
            onClick={() => {
              if (!option.disabled) {
                handleSelect(option.value);
              }
            }}
            role="option"
            aria-selected={option.value === value}
            aria-disabled={option.disabled}
            className={cn(
              DEFAULT_STYLES.select__bottomSheet__option,
              option.value === value &&
              DEFAULT_STYLES.select__bottomSheet__optionSelected,
              option.disabled &&
              DEFAULT_STYLES.select__bottomSheet__optionDisabled,
              optionClassName
            )}
          >
            <div className="flex items-center justify-between w-full">
              <span className="flex-1 break-words whitespace-normal">
                {option.label}
              </span>
            </div>
          </div>
        ))
      ) : (
        <div className={DEFAULT_STYLES.dropdown__noResults}>
          No options found
        </div>
      )}
    </div>
  );

  // Render desktop options
  const renderDesktopOptions = () => (
    <ul
      ref={listBoxRef}
      role="listbox"
      aria-labelledby={label ? `${name}-label` : undefined}
      className="py-2 px-2 overflow-y-auto outline-none"
      style={{ maxHeight: 'inherit' }}
    >
      {options.map((option, index) => (
        <li
          key={option.value}
          role="option"
          aria-selected={option.value === value}
          aria-disabled={option.disabled}
          onClick={() => {
            if (!option.disabled) {
              handleSelect(option.value);
            }
          }}
          onMouseEnter={() => setHighlightedIndex(index)}
          className={cn(
            DEFAULT_STYLES.dropdown__option,
            option.value === value && DEFAULT_STYLES.dropdown__optionSelected,
            option.disabled && DEFAULT_STYLES.dropdown__optionDisabled,
            highlightedIndex === index &&
            !option.disabled &&
            'bg-bottomSheetOptionBg',
            optionClassName
          )}
        >
          <div className="flex items-center justify-between w-full">
            <span className="flex-1 break-words whitespace-normal">
              {option.label}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div className={cn(DEFAULT_STYLES.field, className, 'base__select')}>
      {label && (
        <label
          id={`${name}-label`}
          className={cn(
            DEFAULT_STYLES.field__label,
            isDisabled && DEFAULT_STYLES.field__labelDisabled,
            labelClassName
          )}
        >
          {label}
          {isRequired && (
            <span className={DEFAULT_STYLES.field__labelRequired}>*</span>
          )}
        </label>
      )}

      {/* Hidden input for form submission */}
      <input type="hidden" name={name} value={value || ''} />

      <div className="relative">
        {/* Trigger Button */}
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-labelledby={label ? `${name}-label` : undefined}
          aria-disabled={isDisabled}
          disabled={isDisabled}
          onClick={() => handleOpenChange(!isOpen)}
          onKeyDown={handleKeyDown}
          className={cn(
            DEFAULT_STYLES.select__dropdown__trigger,
            showError
              ? 'border-errorRed'
              : isOpen
                ? DEFAULT_STYLES.select__dropdown__triggerOpen
                : DEFAULT_STYLES.field__inputContainerDefault,
            isDisabled && DEFAULT_STYLES.field__inputContainerDisabled,
            'items-start'
          )}
        >
          <span
            className={cn(
              !selectedOption
                ? 'text-placeholderText'
                : DEFAULT_STYLES.field__inputText,
              'break-words whitespace-normal flex-1 text-left'
            )}
          >
            {selectedOption?.label || placeholder}
          </span>
          <svg
            className={cn(
              'w-4 h-4 text-cement flex-shrink-0 ml-2 transform transition-transform duration-300 ease-in-out',
              isOpen ? 'rotate-180' : 'rotate-0'
            )}
            fill="none"
            stroke="#475569"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Desktop Dropdown - Rendered via Portal */}
        {isOpen && !isMobile && (
          <Portal>
            <FocusScope contain restoreFocus>
              <div
                ref={popoverRef}
                className={cn(
                  'bg-white border border-lightBlueGray300 rounded-lg shadow-lg overflow-hidden flex flex-col',
                  animationClasses,
                  'base__select__dropdown'
                )}
                style={{
                  ...positionStyles,
                  width: triggerRef.current?.offsetWidth,
                  maxHeight: `${maxHeight}px`,
                  zIndex: 9999
                }}
              >
                <DismissButton onDismiss={() => handleOpenChange(false)} />
                {renderDesktopOptions()}
                <DismissButton onDismiss={() => handleOpenChange(false)} />
              </div>
            </FocusScope>
          </Portal>
        )}

        {/* Mobile Bottom Sheet */}
        {isOpen && isMobile && (
          <BottomSheet
            isOpen={isOpen}
            onClose={() => handleOpenChange(false)}
            title={label}
            showCloseButton={true}
            className={DEFAULT_STYLES.select__bottomSheet}
          >
            {renderMobileContent()}
          </BottomSheet>
        )}
      </div>

      {/* Single feedback container - always rendered to prevent layout shift */}
      {(hasError || showHelperText) && <p
        className={cn(
          DEFAULT_STYLES.field__message,
          showError
            ? 'text-errorTextColor'
            : showHelperText
              ? 'text-helperTextColor'
              : 'invisible h-4'
        )}
        role={showError ? 'alert' : undefined}
      >
        {showError && error ? (
          <FieldErrorMessage message={error} />
        ) : showHelperText ? (
          helperText
        ) : (
          ''
        )}
      </p>
      }
    </div>
  );
};

Select.displayName = 'Select';
